import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, Button, Row, Col, Spin, message, Divider, Typography, Space, Tooltip } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import AxiosOICStat from '../AxiosOICStat';

const { Title, Text } = Typography;

const Settings = () => {
  const [form] = Form.useForm();
  const [initialValues, setInitialValues] = useState(null);
  const [userId, setUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const PROFILE_GET_URL = '/me';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await AxiosOICStat.get(PROFILE_GET_URL, { withCredentials: true });
        if (!mounted) return;
        setUserId(data?.id);
        const init = {
          username: data?.username ?? '',
          email: data?.email ?? '',
          first_name: data?.first_name ?? '',
          last_name: data?.last_name ?? '',
          password: '',
          confirm_password: '',
        };
        setInitialValues(init);
        form.setFieldsValue(init);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 401) message.error('Session expired. Please log in again.');
        else message.error('Failed to load profile.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [form]);

  const passwordRules = useMemo(() => [{ min: 8, message: 'Password must be at least 8 characters.' }], []);

  const startEdit = (key) => setEditingKey(key);

  const cancelEdit = () => {
    if (!initialValues) return;
    const reset = { [editingKey]: initialValues[editingKey] };
    if (editingKey === 'password') Object.assign(reset, { password: '', confirm_password: '' });
    if (editingKey === 'confirm_password') Object.assign(reset, { confirm_password: '' });
    form.setFieldsValue(reset);
    setEditingKey(null);
  };

  const saveField = async (key) => {
    if (!userId) return;
    try {
      setSaving(true);
      if (key === 'password') {
        await form.validateFields(['password', 'confirm_password']);
        const newPw = form.getFieldValue('password') || '';
        if (!newPw) {
          message.info('Nothing to update.');
          setSaving(false);
          return;
        }
        await AxiosOICStat.put(`/users/${userId}`, { password: newPw }, { withCredentials: true });
        message.success('Password updated.');
        const nextInitial = { ...initialValues, password: '', confirm_password: '' };
        setInitialValues(nextInitial);
        form.setFieldsValue({ password: '', confirm_password: '' });
        setEditingKey(null);
        return;
      }
      await form.validateFields([key]);
      const value = form.getFieldValue(key);
      if (value === initialValues[key]) {
        message.info('Nothing to update.');
        setSaving(false);
        return;
      }
      await AxiosOICStat.put(`/users/${userId}`, { [key]: value }, { withCredentials: true });
      message.success('Profile updated.');
      const nextInitial = { ...initialValues, [key]: value };
      setInitialValues(nextInitial);
      setEditingKey(null);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) message.error('Session expired. Please log in again.');
      else if (status === 404) message.error('User not found.');
      else if (status === 409) message.error('This email is already in use.');
      else if (status === 422) message.error('Validation error.');
      else message.error('Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const Suffix = (field) => (
    <Space.Compact>
      {editingKey === field ? (
        <>
          <Tooltip title="Save">
            <Button type="primary" icon={<SaveOutlined />} onClick={() => saveField(field)} loading={saving} />
          </Tooltip>
          <Tooltip title="Cancel">
            <Button icon={<CloseOutlined />} onClick={cancelEdit} disabled={saving} />
          </Tooltip>
        </>
      ) : (
        <Tooltip title="Edit">
          <Button icon={<EditOutlined />} onClick={() => startEdit(field)} />
        </Tooltip>
      )}
    </Space.Compact>
  );

  if (loading) {
    return (
      <Row style={{ height: '60vh' }} align="middle" justify="center">
        <Spin />
      </Row>
    );
  }

  return (
    <Row style={{ marginTop: 24 }} justify="center">
      <Col xs={24} sm={20} md={16} lg={12} xl={10}>
        <Card style={{ boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }} bordered={false}>
          <Title level={3} style={{ marginBottom: 8 }}>Settings</Title>
          <Text type="secondary">Update your account information and password.</Text>
          <Divider />
          <Form layout="vertical" form={form} initialValues={initialValues || {}}>
            <Form.Item
              label="Username"
              name="username"
              rules={[{ required: true, message: 'Please enter a username.' }]}
            >
              <Input
                placeholder="Username"
                disabled={editingKey !== 'username'}
                suffix={Suffix('username')}
              />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please enter your email.' },
                { type: 'email', message: 'Please enter a valid email.' },
              ]}
            >
              <Input
                placeholder="you@example.com"
                type="email"
                disabled={editingKey !== 'email'}
                suffix={Suffix('email')}
              />
            </Form.Item>

            <Form.Item
              label="First Name"
              name="first_name"
              rules={[{ required: true, message: 'Please enter your first name.' }]}
            >
              <Input
                placeholder="First name"
                disabled={editingKey !== 'first_name'}
                suffix={Suffix('first_name')}
              />
            </Form.Item>

            <Form.Item
              label="Last Name"
              name="last_name"
              rules={[{ required: true, message: 'Please enter your last name.' }]}
            >
              <Input
                placeholder="Last name"
                disabled={editingKey !== 'last_name'}
                suffix={Suffix('last_name')}
              />
            </Form.Item>

            <Divider>Change Password</Divider>

            <Form.Item
              label="New Password"
              name="password"
              rules={passwordRules}
              hasFeedback
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Input.Password
                  placeholder="New password"
                  disabled={editingKey !== 'password'}
                  visibilityToggle={true}
                  style={{ flex: 1, marginRight: '8px' }}
                />
                {Suffix('password')}
              </div>
            </Form.Item>

            <Form.Item
              label="Confirm New Password"
              name="confirm_password"
              dependencies={['password']}
              hasFeedback
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const pw = getFieldValue('password');
                    if (editingKey !== 'password') return Promise.resolve();
                    if (!pw && !value) return Promise.resolve();
                    if (pw && value === pw) return Promise.resolve();
                    return Promise.reject(new Error('Passwords do not match.'));
                  },
                }),
              ]}
            >
              <Input.Password 
                placeholder="Re-enter new password" 
                disabled={editingKey !== 'password'} 
                visibilityToggle={true}
                style={{ width: '90%' }}
              />
            </Form.Item>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};

export default Settings;
